import { useMemo, useState } from 'react';

import type { AgentSession, AgentSessionStatus } from '@/api-models/agent-sessions';
import type { Repository } from '@/api-models/repositories';
import {
  applyFilters,
  toggleFilterValueIn,
  type FieldDef,
  type FieldOption,
  type SortSpec,
  type ViewFilter,
} from '@/shared/shell/view-controls';

export type SessionFilterField = 'status' | 'project' | 'branch';
export type SessionGroupField = 'status' | 'project' | 'branch' | 'none';
export type SessionSortField = 'updated_at' | 'created_at' | 'name';

export type SessionFilter = ViewFilter<SessionFilterField>;
export type SessionSort = SortSpec<SessionSortField>;
export type SessionFieldOptions = Record<SessionFilterField, FieldOption[]>;

export const SESSION_FILTER_FIELDS: FieldDef<SessionFilterField>[] = [
  { field: 'status', label: 'Status' },
  { field: 'project', label: 'Repository' },
  { field: 'branch', label: 'Branch' },
];

export const SESSION_GROUP_FIELDS: FieldDef<SessionGroupField>[] = [
  { field: 'status', label: 'Status' },
  { field: 'project', label: 'Repository' },
  { field: 'branch', label: 'Branch' },
  { field: 'none', label: 'No grouping' },
];

export const SESSION_SORT_FIELDS: FieldDef<SessionSortField>[] = [
  { field: 'updated_at', label: 'Last updated' },
  { field: 'created_at', label: 'Created' },
  { field: 'name', label: 'Name' },
];

const NEUTRAL = '#6e7390';
const NO_BRANCH = 'none';

const STATUS_ORDER: AgentSessionStatus[] = ['running', 'provisioning', 'planning', 'hibernated', 'error'];
const STATUS_LABEL: Record<AgentSessionStatus, string> = {
  running: 'Running',
  provisioning: 'Starting',
  planning: 'Plan',
  hibernated: 'Paused',
  error: 'Error',
};
const STATUS_COLOR: Record<AgentSessionStatus, string> = {
  running: '#3dd68c',
  provisioning: '#46b6f0',
  planning: '#a78bfa',
  hibernated: '#6e7390',
  error: '#ef5a5a',
};

/** The label a session row shows, and the haystack the search box matches on. */
export const sessionTitle = (session: AgentSession) => session.name || session.initial_prompt || 'Untitled session';

const UNKNOWN_REPO = 'Unknown repository';

/** `repository_id` → repo label (`owner/repo`, falling back to the repository name). */
export function makeRepoLabel(repositories: Repository[]): (repositoryId: string) => string {
  const names = new Map(repositories.map((r) => [r.id, r.repo_full_name ?? r.name]));
  return (repositoryId) => names.get(repositoryId) ?? UNKNOWN_REPO;
}

function valueOfField(session: AgentSession, field: SessionFilterField): string {
  switch (field) {
    case 'status':
      return session.status;
    case 'project':
      return session.repository_id;
    case 'branch':
      return session.branch || NO_BRANCH;
  }
}

function valuesOfField(session: AgentSession, field: SessionFilterField): string[] {
  return [valueOfField(session, field)];
}

function deriveFieldOptions(sessions: AgentSession[], repositories: Repository[]): SessionFieldOptions {
  const repoLabel = makeRepoLabel(repositories);

  const repositoryIds = [...new Set(sessions.map((s) => s.repository_id))];
  const project = repositoryIds
    .map((id) => ({ value: id, label: repoLabel(id), color: NEUTRAL }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const branchNames = [...new Set(sessions.map((s) => s.branch).filter((b): b is string => !!b))].sort((a, b) =>
    a.localeCompare(b),
  );
  const branch = branchNames.map((name) => ({ value: name, label: name, color: NEUTRAL }));
  if (sessions.some((s) => !s.branch)) branch.push({ value: NO_BRANCH, label: 'No branch', color: NEUTRAL });

  const status = STATUS_ORDER.map((s) => ({ value: s, label: STATUS_LABEL[s], color: STATUS_COLOR[s] }));

  const raw: Record<SessionFilterField, Omit<FieldOption, 'count'>[]> = { status, project, branch };
  const result = {} as SessionFieldOptions;
  for (const field of Object.keys(raw) as SessionFilterField[]) {
    const counts = new Map<string, number>();
    for (const session of sessions) {
      const value = valueOfField(session, field);
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    result[field] = raw[field].map((opt) => ({ ...opt, count: counts.get(opt.value) ?? 0 }));
  }
  return result;
}

function matchesSearch(session: AgentSession, needle: string): boolean {
  const haystack = [session.name, session.initial_prompt, session.branch].filter(Boolean).join(' ').toLowerCase();
  return haystack.includes(needle);
}

function makeComparator(sort: SessionSort): (a: AgentSession, b: AgentSession) => number {
  const dir = sort.dir === 'asc' ? 1 : -1;
  return (a, b) => {
    let delta = 0;
    if (sort.field === 'name') delta = sessionTitle(a).localeCompare(sessionTitle(b));
    else delta = Date.parse(a[sort.field]) - Date.parse(b[sort.field]);
    if (delta === 0) delta = Date.parse(a.updated_at) - Date.parse(b.updated_at);
    return delta * dir;
  };
}

export interface SessionGroup {
  key: string;
  label: string;
  /** Dot/accent color (raw hex for inline styles). */
  color: string;
  sessions: AgentSession[];
}

function buildGroups(
  sessions: AgentSession[],
  groupField: SessionGroupField,
  sort: SessionSort,
  fieldOptions: SessionFieldOptions,
  keepEmpty: boolean,
): { groups: SessionGroup[]; grouped: boolean } {
  const compare = makeComparator(sort);

  if (groupField === 'none') {
    return {
      groups: [{ key: 'all', label: 'All sessions', color: NEUTRAL, sessions: [...sessions].sort(compare) }],
      grouped: false,
    };
  }

  const buckets = new Map<string, AgentSession[]>();
  for (const session of sessions) {
    const key = valueOfField(session, groupField);
    const arr = buckets.get(key) ?? [];
    arr.push(session);
    buckets.set(key, arr);
  }

  const groups = fieldOptions[groupField]
    .map((opt) => ({
      key: opt.value,
      label: opt.label,
      color: opt.color,
      sessions: [...(buckets.get(opt.value) ?? [])].sort(compare),
    }))
    .filter((g) => keepEmpty || g.sessions.length > 0);
  return { groups, grouped: true };
}

/** Filter / search / group / sort state for the sessions list. Mirrors `useBoardControls`. */
export function useSessionControls(sessions: AgentSession[], repositories: Repository[]) {
  const [filters, setFilters] = useState<SessionFilter[]>([]);
  const [search, setSearch] = useState('');
  const [groupField, setGroupField] = useState<SessionGroupField>('status');
  const [sort, setSort] = useState<SessionSort>({ field: 'updated_at', dir: 'desc' });
  const [showEmptyGroups, setShowEmptyGroups] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  // Archived sessions are excluded up front, so the filter/group value counts
  // describe what the view actually shows.
  const visible = useMemo(
    () => (showArchived ? sessions : sessions.filter((s) => !s.archived_at)),
    [sessions, showArchived],
  );
  const fieldOptions = useMemo(() => deriveFieldOptions(visible, repositories), [visible, repositories]);
  const filtered = useMemo(() => {
    const matched = applyFilters(visible, filters, valuesOfField);
    const needle = search.trim().toLowerCase();
    return needle ? matched.filter((s) => matchesSearch(s, needle)) : matched;
  }, [visible, filters, search]);
  const { groups, grouped } = useMemo(
    () => buildGroups(filtered, groupField, sort, fieldOptions, showEmptyGroups),
    [filtered, groupField, sort, fieldOptions, showEmptyGroups],
  );

  return {
    filters,
    search,
    groupField,
    sort,
    showEmptyGroups,
    showArchived,
    fieldOptions,
    groups,
    grouped,
    filteredCount: filtered.length,
    setSearch,
    toggleFilterValue: (field: SessionFilterField, value: string) =>
      setFilters((prev) => toggleFilterValueIn(prev, field, value)),
    changeFilter: (id: string, patch: Partial<SessionFilter>) =>
      setFilters((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f))),
    removeFilter: (id: string) => setFilters((prev) => prev.filter((f) => f.id !== id)),
    setGroupField,
    setSort,
    toggleEmptyGroups: () => setShowEmptyGroups((v) => !v),
    toggleArchived: () => setShowArchived((v) => !v),
  };
}
